/**
 * 视口工具 - 解决移动端浏览器100vh/vw兼容性问题
 * 
 * 移动端浏览器地址栏会影响100vh的计算，导致内容溢出
 * 本工具通过CSS变量提供精确的视口尺寸
 * 
 * 使用方法：
 * 1. 在main.js中调用 initViewport()
 * 2. 在CSS中使用 calc(var(--vh, 1vh) * 100) 代替 100vh
 * 3. 在JS中使用 getViewportHeight() 和 getViewportWidth() 获取尺寸
 */

let viewportHeight = 0
let viewportWidth = 0
let isInitialized = false

// 全屏保护锁：进入全屏 API 调用前设置，fullscreenchange 触发后清除
// 用于拦截 Safari 在 fullscreenchange 之前触发的 resize/visualViewport resize
// 此时 document.fullscreenElement 还是 null，isNativeFullscreen 检查无效
let isEnteringFullscreen = false

/**
 * 设置全屏保护锁（进入全屏 API 调用前调用）
 */
export function lockViewportForFullscreen() {
  isEnteringFullscreen = true
}

/**
 * 清除全屏保护锁（fullscreenchange 回调中调用）
 */
export function unlockViewportForFullscreen() {
  isEnteringFullscreen = false
}

/**
 * 查询是否处于全屏进入过渡保护期（lockViewportForFullscreen 已调用但 fullscreenchange 未触发）
 */
export function isEnteringFullscreenLock() {
  return isEnteringFullscreen
}

/**
 * iPhone / iPad / iPadOS 13+（MacIntel + 多点触控）
 * @returns {boolean}
 */
export function isAppleTouchDevice () {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return true
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1
}

/**
 * 是否展示「全屏」按钮（含 iPad 原生 API；失败时 Game.vue 降级 CSS 沉浸）。
 * @returns {boolean}
 */
export function shouldShowNativeFullscreenButton () {
  if (typeof document === 'undefined') return true
  if (isAppleTouchDevice()) return true
  const el = document.documentElement
  if (!el) return true
  return !!(
    typeof el.requestFullscreen === 'function' ||
    typeof el.webkitRequestFullscreen === 'function' ||
    typeof el.mozRequestFullScreen === 'function' ||
    typeof el.msRequestFullscreen === 'function'
  )
}

/**
 * 触摸设备原生全屏期间是否应禁止对 input 调用 focus。
 *
 * 实机结论（iPad）：文本 input 获焦会退出 Fullscreen API（现象 A「一闪就退」）。
 * 故 Apple / 其它手机平板在原生全屏下均禁焦；输入走 key-sink + Game.handleFullscreenKeyboardFallback。
 * PC 不走本分支。CSS 沉浸（无 fullscreenElement）不走本函数的禁焦分支。
 *
 * @returns {boolean}
 */
export function shouldBlockNativeFocusInFullscreen () {
  if (!isInFullscreen()) return false
  return isMobile() || isTablet() || isAppleTouchDevice()
}

/**
 * 查询当前是否处于全屏过渡保护期或原生全屏状态
 * 供其他组件（如 QuestionInput）检测全屏状态，包括 fullscreenchange 触发前的窗口期
 */
export function isInFullscreen() {
  return isEnteringFullscreen || !!(document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement)
}

/**
 * 更新视口尺寸并设置CSS变量
 */
function updateViewportSize() {
  // 全屏保护锁：进入全屏 API 调用前已设置，此时 fullscreenchange 可能还未触发
  // document.fullscreenElement 尚为 null，isNativeFullscreen 检查无效，必须用此锁拦截
  if (isEnteringFullscreen) return

  // 全屏期间跳过更新，避免 CSS 变量修改干扰浏览器全屏机制
  const isNativeFullscreen = !!(document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement)
  if (isNativeFullscreen) return

  const newHeight = window.innerHeight
  const newWidth = window.innerWidth
  
  // 检测尺寸是否变化
  const changed = newHeight !== viewportHeight || newWidth !== viewportWidth
  
  viewportHeight = newHeight
  viewportWidth = newWidth
  
  // 计算1vh和1vw对应的像素值
  const vh = viewportHeight * 0.01
  const vw = viewportWidth * 0.01
  
  // 设置CSS自定义属性（变量）
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  document.documentElement.style.setProperty('--vw', `${vw}px`)
  
  // 同时设置完整视口尺寸变量
  document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`)
  document.documentElement.style.setProperty('--viewport-width', `${viewportWidth}px`)
  
  if (changed) {
    // console.log('📐 [viewport] 视口更新:', { width: viewportWidth, height: viewportHeight })
  }
}

/**
 * 防抖函数
 */
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * 初始化视口工具
 * 监听窗口大小变化并更新CSS变量
 */
export function initViewport() {
  if (isInitialized) {
    return
  }
  
  // 立即执行一次更新
  updateViewportSize()
  
  // 监听窗口大小变化（带防抖）
  const debouncedUpdate = debounce(updateViewportSize, 150)
  window.addEventListener('resize', debouncedUpdate, { passive: true })
  
  // 监听屏幕方向变化 - 优先使用新API
  if (window.screen && window.screen.orientation) {
    window.screen.orientation.addEventListener('change', updateViewportSize)
  } else {
    // 降级方案
    window.addEventListener('orientationchange', updateViewportSize)
  }
  
  // 监听视觉视口变化（处理软键盘弹出等场景）
  if ('visualViewport' in window) {
    window.visualViewport.addEventListener('resize', debouncedUpdate)
  }
  
  isInitialized = true
  // console.log('✅ [viewport] 视口工具已初始化')
}

/**
 * 获取当前视口高度（像素）
 * @returns {number} 视口高度
 */
export function getViewportHeight() {
  return viewportHeight || window.innerHeight
}

/**
 * 获取当前视口宽度（像素）
 * @returns {number} 视口宽度
 */
export function getViewportWidth() {
  return viewportWidth || window.innerWidth
}

/**
 * 检测设备信息（基于User-Agent + 触摸屏）
 * @returns {Object} 设备信息
 */
function detectDeviceInfo() {
  const ua = navigator.userAgent || ''
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  
  // 手机端 UA
  const isMobileUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  
  // 平板端 UA
  const isTabletUA = /iPad|Android(?!.*Mobile)/i.test(ua)
  
  return {
    ua,
    hasTouch,
    isMobileUA,
    isTabletUA
  }
}

/**
 * 判断是否为移动端（综合UA + 触摸屏 + 宽度）
 * @returns {boolean}
 */
export function isMobile() {
  const width = getViewportWidth()
  const { isMobileUA, hasTouch } = detectDeviceInfo()
  
  // 满足 UA 判断且宽度 <= 768，认为是手机
  if (isMobileUA && width <= 768) return true
  
  // 有触摸屏且宽度 <= 768，认为是手机
  if (hasTouch && width <= 768) return true
  
  // 纯宽度判断（兜底）
  return width <= 768
}

/**
 * 判断是否为平板（综合UA + 触摸屏 + 宽度范围判断）
 * @returns {boolean}
 */
export function isTablet() {
  const width = getViewportWidth()
  const { isTabletUA, isMobileUA, hasTouch } = detectDeviceInfo()
  
  // 条件1：UA 明确是平板（iPad或Android平板）
  if (isTabletUA) return true
  
  // 条件2：有触摸屏 + 非手机UA + 宽度大于等于768
  // iPadOS 13+ 伪装 Mac UA，竖屏宽度恰好为768px，用 >= 避免边界漏判
  if (hasTouch && !isMobileUA && width >= 768) return true
  
  return false
}

/**
 * 判断是否为PC端（综合UA + 触摸屏）
 * @returns {boolean}
 */
export function isDesktop() {
  const width = getViewportWidth()
  const { isTabletUA, isMobileUA, hasTouch } = detectDeviceInfo()
  
  // 手机或平板 UA 则不是 PC
  if (isMobileUA || isTabletUA) return false
  
  // 无触摸屏且宽度 > 768，认为是 PC
  if (!hasTouch && width > 768) return true
  
  // 有触摸屏但宽度 > 1280，也认为是PC（大屏触摸设备）
  if (hasTouch && width > 1280) return true
  
  return false
}

/**
 * 获取设备类型（综合多维度判断）
 * @returns {'mobile' | 'tablet' | 'desktop'}
 */
export function getDeviceType() {
  const width = getViewportWidth()
  const { isMobileUA, isTabletUA, hasTouch } = detectDeviceInfo()
  
  // 规则1：手机端 UA + 宽度小 = 手机
  if (isMobileUA && width <= 768) {
    return 'mobile'
  }
  
  // 规则2：平板端 UA = 平板
  if (isTabletUA) {
    return 'tablet'
  }
  
  // 规则3：有触摸屏 + 非手机UA + 宽度 > 768 = 平板
  if (hasTouch && !isMobileUA && width > 768) {
    return 'tablet'
  }
  
  // 规则4：有触摸 + 宽度小 = 手机
  if (hasTouch && width <= 768) {
    return 'mobile'
  }
  
  // 规则5：无触摸 + 宽度 > 768 = PC
  if (!hasTouch && width > 768) {
    return 'desktop'
  }
  
  // 规则6：有触摸 + 宽度很大 = 大屏PC
  if (hasTouch && width > 1280) {
    return 'desktop'
  }
  
  // 兜底：按宽度判断
  if (width <= 768) {
    return 'mobile'
  }
  
  return 'tablet'
}

/**
 * 强制刷新视口尺寸缓存
 * 可在需要时手动调用
 */
export function refreshViewport() {
  updateViewportSize()
}

// 默认导出
export default {
  initViewport,
  getViewportHeight,
  getViewportWidth,
  isMobile,
  isTablet,
  isDesktop,
  getDeviceType,
  refreshViewport,
  shouldShowNativeFullscreenButton,
  isAppleTouchDevice,
  shouldBlockNativeFocusInFullscreen
}
